import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertCircle, Loader } from "lucide-react";
import apiClient from "../../utils/apiClient";
import "./marketing.scss";

/**
 * /marketing/connect/relay — the one URL registered with Google.
 *
 * Google matches `redirect_uri` **exactly** and supports no wildcards, so
 * without this page every institute subdomain and every custom domain needs its
 * own entry in the Cloud console — added by hand, forever, and failing silently
 * the first time somebody forgets. That is the identical trap
 * [`WhatsAppConnectRelay`](../WhatsApp/WhatsAppConnectRelay.jsx) exists for on
 * Meta's side, which is why this page is its twin and why both resolve their
 * state through the same backend endpoint rather than two of them.
 *
 * Deliberately the thinnest possible page:
 *
 *   • it never exchanges the code — that needs a session, and this page has none
 *   • it never decides the return origin — the backend signed that into `state`
 *   • it renders nothing on the happy path, because it is gone in a moment
 *
 * **Public by necessity.** It runs on the canonical host, in the popup Google
 * redirected, where the institute admin has no session: their token lives in
 * `localStorage` on their *own* origin. Wrapping this route in `ProtectedRoute`
 * would bounce the popup to a login screen on the wrong domain — the exact
 * failure it exists to prevent.
 */
const MarketingConnectRelay = () => {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState("");
  const bouncedRef = useRef(false);

  useEffect(() => {
    // The redirect must fire exactly once; React 18 StrictMode double-invokes.
    if (bouncedRef.current) return;
    bouncedRef.current = true;

    const code = searchParams.get("code");
    const state = searchParams.get("state") || "";
    const oauthError = searchParams.get("error");

    /**
     * A denial is a completed flow, not a broken one. Forwarding it home means
     * the institute sees "you cancelled" on their own screen instead of a bare
     * error on a domain they do not recognise.
     */
    if (oauthError) {
      forward(
        window.location.origin,
        { error: searchParams.get("error_description") || oauthError }
      );
      return;
    }

    if (!code) {
      setError("No authorization code was returned by Google.");
      return;
    }

    (async () => {
      /**
       * An **empty** state means the backend saw no reason to mint one: the
       * dialog was returning to the origin it left, which is the institute whose
       * LMS is served from the canonical host itself. Treating that as an error
       * would break precisely the customer for whom the relay was never needed.
       */
      if (!state) {
        forward(window.location.origin, { code });
        return;
      }

      try {
        // The same resolver the Meta relay uses — it answers "which origin did
        // this flow start from", which is provider-neutral.
        const { data } = await apiClient.post("/whatsapp/connect/resolve-state", { state });
        if (!data?.origin) {
          setError("This connect link is not valid. Close this window and try again.");
          return;
        }
        forward(data.origin, { code, state });
      } catch (err) {
        setError(
          err?.response?.data?.message ||
            "Could not complete the connection. Close this window and try again."
        );
      }
    })();
  }, [searchParams]);

  /**
   * `replace`, not `assign` — the authorization code is in this URL, and a
   * history entry on the canonical host would keep it reachable by Back long
   * after it was spent.
   */
  const forward = (origin, params) => {
    const url = new URL("/marketing/connect/google", origin);
    Object.entries(params).forEach(([k, v]) => v && url.searchParams.set(k, v));
    window.location.replace(url.toString());
  };

  return (
    <div className="mkt-callback">
      {error ? (
        <>
          <AlertCircle size={40} className="mkt-callback__icon mkt-callback__icon--error" />
          <h3 className="mkt-callback__title">Connection failed</h3>
          <p className="mkt-callback__text">{error}</p>
          <p className="mkt-callback__text">You can close this window.</p>
        </>
      ) : (
        <>
          <Loader size={40} className="mkt-callback__icon mkt-callback__icon--working" />
          <h3 className="mkt-callback__title">Finishing up…</h3>
          <p className="mkt-callback__text">Returning you to your dashboard.</p>
        </>
      )}
    </div>
  );
};

export default MarketingConnectRelay;
